import React from 'react';

import { describeChannelReason } from '../../config/notificationChannels';

/**
 * The three delivery cells at the right of a notification row: one joined
 * 32px outline, a cell per channel, divided by hairlines.
 *
 * It is a visual SIBLING of `Segmented` and explicitly not one. A Segmented
 * is single-choice — picking Email would unpick Text. This is three
 * independent booleans, and the platform has no control for that, so the bank
 * borrows Segmented's frame (the joined outline, the shared radius, the 32px
 * height) and nothing else. Each cell is a real `role="switch"` button whose
 * accessible name carries BOTH the channel and the notification, because a
 * reader tabbing through ninety of them hears the channel over and over and
 * needs to know which row they are standing in.
 *
 * The four states, and why each looks the way it does:
 *
 *   ON        `--action-tint` ground, `--action-deep` glyph — the applied-
 *             filter idiom, so nothing is invented and a column of ONs reads
 *             across thirty rows at a glance.
 *   OFF       transparent, `--ink-muted` glyph. NOT `--decor`: an off state
 *             is an answer somebody gave, not decoration.
 *   DISABLED  `--chip-bg` + `--decor`, `cursor: not-allowed`, and out of the
 *             tab order — there is nothing here to operate. The REASON is not
 *             on the cell: it sits once above the whole list, and every
 *             disabled cell points `aria-describedby` at that one paragraph,
 *             so a screen reader hears it in context rather than ninety times.
 *   FORCED    on, in the action tint, cursor default, `aria-disabled` so it
 *             STAYS focusable and its lock reason can be read. **Never
 *             greyed**: a grey switch reads as broken, and this one is
 *             working exactly as the collection intends.
 */
export default function ChannelBank({
	row,
	channels,
	onToggle,
	busyChannel = null,
	readOnly = false,
	unavailableHintId,
	readOnlyHintId,
	lockReasonId,
}) {
	const values = row?.channels || {};
	const name = row?.name || 'this notification';

	return (
		<div className="channel-bank" role="group" aria-label={`Delivery for ${name}`}>
			{channels.map(channel => {
				const state = values[channel.key] || {};
				const value = state.value === true;
				const locked = Boolean(state.locked);
				// Two different kinds of "you can't". A channel that does not
				// deliver is DEAD — greyed, because there is nothing behind
				// it. A read-only list is BLOCKED — the value still reads
				// true or false, it is simply not yours to change. Locked
				// cells are neither: they keep their focus so the reason is
				// reachable.
				const dead = !channel.available;
				const blocked = !dead && readOnly;
				const Glyph = channel.glyph;

				const classes = [
					'channel-bank__cell',
					value ? 'channel-bank__cell--on' : 'channel-bank__cell--off',
					locked ? 'channel-bank__cell--forced' : '',
					dead ? 'channel-bank__cell--dead' : '',
					blocked ? 'channel-bank__cell--blocked' : '',
				].filter(Boolean).join(' ');

				const describedBy = [
					dead ? unavailableHintId : null,
					blocked ? readOnlyHintId : null,
					locked ? lockReasonId : null,
				].filter(Boolean).join(' ');

				return (
					<button
						key={channel.key}
						type="button"
						role="switch"
						aria-checked={value}
						// The name says both halves: "Email — Background check expiring".
						aria-label={`${channel.label} — ${name}`}
						className={classes}
						disabled={dead || blocked}
						aria-disabled={locked || undefined}
						aria-describedby={describedBy || undefined}
						title={locked
							? (row?.lockReason || 'This one is required.')
							: (dead ? describeChannelReason(channel.reason) : undefined)}
						data-busy={busyChannel === channel.key ? 'true' : undefined}
						onClick={() => {
							if (dead || blocked || locked) return;
							onToggle(channel.key, !value);
						}}
					>
						<Glyph className="channel-bank__glyph" aria-hidden="true" />
					</button>
				);
			})}
		</div>
	);
}
